import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/axiosConfig';
import { toLocalDateString } from '../utils/localDate';
import { STATE, stateOf, fmtTime, fmtLateness } from '../utils/slotState';
import { DAY_LABELS, fmtDayRange, fmtDayWithWeekday } from '../utils/formatDate';

const kstDate = (iso) => new Date(iso);

const InspectionMatrixPage = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const weekOf = searchParams.get('weekOf');

    const [data, setData] = useState(null);
    /** 이 주에 낀 공휴일. [{ date, name }] */
    const [holidays, setHolidays] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    /**
     * 열어 둔 칸은 주소에 적는다.
     *
     * 예전에는 화면 안의 값이었다. 기록 하나를 열면 화면이 갈아치워지고,
     * 돌아오면 칸이 닫혀 있어 매번 다시 찾아 눌러야 했다. 주소에 있으면 뒤로
     * 가기가 열어 둔 자리로 돌려놓고, 이 화면을 그대로 링크로 넘길 수도 있다.
     *
     * 날짜 칸은 `p<계획>d<요일>`, 날짜가 없는 회차는 `s<회차>`. 회차 id 만으로
     * 적으면 한 칸에 세 회차가 있는 날 무엇을 펼쳤었는지 되살릴 수 없다.
     */
    const cellKey = searchParams.get('cell');
    const openDetail = (key) =>
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('cell', key);
            return next;
        });
    const closeDetail = () =>
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('cell');
            return next;
        });

    /**
     * 'YYYY-MM-DD' -> 공휴일 이름.
     *
     * 날짜는 서버가 정해서 보낸다(`day`, `columnDays`). 여기서 시각을 날짜로
     * 바꾸면 시간대를 한 번 더 다루게 되고, KST 자정이 UTC 로는 전날이라
     * 하루씩 밀린다 -- 실제로 그렇게 밀렸다.
     */
    const holidayByDay = useMemo(() => {
        const map = new Map();
        for (const h of holidays) {
            if (h.day) map.set(h.day, h.name);
        }
        return map;
    }, [holidays]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.get('/inspection-plans/slots/matrix', {
                params: weekOf ? { weekOf } : {},
            });
            setData(res.data);

            // 공휴일은 숫자를 고치지 않는다. 낮게 나온 까닭을 말해 줄 뿐이다.
            // 못 받아도 표만 그대로 그려지면 되므로 실패는 흘린다.
            api
                .get('/holidays', {
                    params: { from: res.data.period.start, to: res.data.period.end },
                })
                .then((h) => setHolidays(h.data || []))
                .catch(() => setHolidays([]));
        } catch (err) {
            console.error('Failed to load inspection matrix', err);
            setError('점검 현황을 불러오지 못했습니다.');
        } finally {
            setLoading(false);
        }
    }, [weekOf]);

    useEffect(() => { fetchData(); }, [fetchData]);

    /** 주소에 적힌 칸을 이번 주 데이터에서 찾는다. 없으면 안 연 것과 같다. */
    const detail = useMemo(() => {
        if (!data || !cellKey) return null;

        const day = /^p(\d+)d(\d+)$/.exec(cellKey);
        if (day) {
            const row = data.rows.find((r) => r.planId === Number(day[1]));
            const cell = row?.cells?.[Number(day[2])];
            if (!row || !cell || cell.slots.length === 0) return null;
            return { row, cell, slots: cell.slots };
        }

        const floating = /^s(\d+)$/.exec(cellKey);
        if (floating) {
            for (const row of data.rows) {
                const slot = row.floating.find((s) => s.id === Number(floating[1]));
                if (slot) return { row, cell: null, slots: [slot] };
            }
        }
        return null;
    }, [data, cellKey]);

    const shiftWeek = (weeks) => {
        const base = data ? kstDate(data.period.start) : new Date();
        base.setDate(base.getDate() + weeks * 7);
        setSearchParams({ weekOf: toLocalDateString(base) });
    };

    const isThisWeek = useMemo(() => {
        if (!data) return true;
        const now = Date.now();
        return now >= kstDate(data.period.start).getTime()
            && now <= kstDate(data.period.end).getTime();
    }, [data]);

    const todayIndex = useMemo(() => {
        if (!data || !isThisWeek) return -1;
        return data.columns.findIndex(
            (c) => kstDate(c).toDateString() === new Date().toDateString(),
        );
    }, [data, isThisWeek]);

    /**
     * Which facility a row is about this period.
     *
     * A rotating plan points at a different building each period, and with more
     * than one client there will be several spare patrols whose names alone do
     * not distinguish them. The plan name is not enough on its own.
     *
     * 한 회차가 두 건물을 걸칠 수 있으므로 들르는 곳들에서 모은다.
     */
    const rowFacilities = (row) => {
        const all = [...row.cells.flatMap((c) => c.slots), ...row.floating];
        return [
            ...new Set(
                all
                    .flatMap((s) => s.stops || [])
                    .map((st) => st.facility?.name)
                    .filter(Boolean),
            ),
        ];
    };

    /**
     * 이 회차가 어디를 도는가.
     *
     * 경로에 이름이 있으면 그것을 쓴다. 없으면 -- 경로가 하나뿐인 계획에서는
     * 굳이 이름을 붙이지 않는다 -- 들르는 곳으로 대신한다.
     */
    const routeText = (slot) => {
        if (slot.route?.name) return slot.route.name;
        const stops = slot.stops || [];
        if (stops.length === 0) return '경로 미정';
        const first = stops[0];
        const place = first.section?.name
            ? `${first.facility?.name} · ${first.section.name}`
            : `${first.facility?.name} 전체`;
        return stops.length > 1 ? `${place} 외 ${stops.length - 1}곳` : place;
    };

    const renderCell = (row, cell, colIndex) => {
        const slots = cell.slots;
        if (slots.length === 0) {
            return <span style={{ color: STATE.EMPTY.color }}>{STATE.EMPTY.symbol}</span>;
        }
        const priority = ['MISSED', 'LATE', 'PENDING', 'ON_TIME'];
        const worst = [...slots].sort(
            (a, b) => priority.indexOf(a.status) - priority.indexOf(b.status),
        )[0];
        const s = stateOf(worst);
        const done = slots.filter((x) => x.status === 'ON_TIME' || x.status === 'LATE').length;
        const anyAtRisk = slots.some((x) => x.atRisk);

        // A day holding several slots -- three guard rounds, say -- shows how
        // many are done rather than one symbol, which would read as though the
        // whole day had failed when only the last round is outstanding.
        // A single slot names its window underneath. Knowing a visit happened on
        // Tuesday is not much use when the plan owes one morning and one
        // afternoon and the row cannot say which of them this was.
        const only = slots.length === 1 ? slots[0] : null;
        const windowLabel = only && only.label !== 'ANYTIME' ? only.labelText : null;

        return (
            <button
                className={`matrix-dot-wrap`}
                onClick={() => openDetail(`p${row.planId}d${colIndex}`)}
                aria-label={`${row.planName} ${DAY_LABELS[colIndex]} ${windowLabel ? `${windowLabel} ` : ''}${slots.length > 1 ? `${done}/${slots.length} 완료` : s.text}`}
            >
                <span
                    className={`matrix-dot ${slots.length > 1 ? 'multi' : ''}`}
                    style={{ background: s.bg, color: s.color, borderColor: anyAtRisk ? '#f59e0b' : 'transparent' }}
                >
                    {slots.length > 1 ? `${done}/${slots.length}` : s.symbol}
                </span>
                {windowLabel && <span className="matrix-when">{windowLabel}</span>}
            </button>
        );
    };

    return (
        <div className="matrix-page pb-5">
            <style>{`
                .matrix-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
                /* 375px 기준: 페이지 여백 32 + 카드 여백 16 을 뺀 327px 안에
                   84 + 7x32 = 308 이 들어가도록 잡았다. */
                .matrix-card { padding: 0.5rem; }
                .matrix-grid {
                    display: grid;
                    grid-template-columns: 84px repeat(7, minmax(32px, 1fr));
                    align-items: center;
                }
                .matrix-head {
                    font-size: 0.75rem; font-weight: 700; color: #6b7280;
                    text-align: center; padding: 0.35rem 0;
                }
                .matrix-head.today { color: var(--primary-color); }
                /* 공휴일은 달력에서 쓰는 빨강을 그대로 쓴다. 새 규칙을 만들면
                   무슨 뜻인지 배워야 하지만, 이건 아무도 안 배워도 안다. */
                .matrix-head.holiday { color: #dc2626; }
                .holiday-dot { display: block; line-height: 0.4; }
                .matrix-rowlabel {
                    font-size: 0.75rem; font-weight: 700; line-height: 1.25;
                    padding: 0.6rem 0.3rem 0.6rem 0; color: #111827;
                    /* 한글은 어절 단위로 끊어야 '정기순/회' 같은 줄바꿈이 안 생긴다.
                       다만 '경비근무자순찰리스트'처럼 띄어쓰기 없는 긴 이름은
                       끊지 않으면 칸 영역을 침범하므로 그때만 강제로 넘긴다. */
                    word-break: keep-all;
                    overflow-wrap: break-word;
                    min-width: 0;
                }
                .matrix-rowlabel .target {
                    display: block; font-weight: 600; font-size: 0.68rem;
                    color: var(--primary-color); margin-top: 1px;
                }
                .matrix-rowlabel .sub {
                    display: block; font-weight: 500; font-size: 0.66rem; color: #9ca3af;
                }
                .matrix-cell { display: flex; justify-content: center; padding: 0.45rem 0; }
                .matrix-dot {
                    width: 26px; height: 26px; border-radius: 50%;
                    border: 2px solid transparent; padding: 0;
                    font-size: 0.85rem; font-weight: 700; line-height: 1;
                    display: flex; align-items: center; justify-content: center;
                    position: relative; cursor: pointer;
                }
                .matrix-dot.multi { font-size: 0.62rem; letter-spacing: -0.03em; }
                .matrix-dot-wrap {
                    background: none; border: 0; padding: 0; cursor: pointer;
                    display: flex; flex-direction: column; align-items: center; gap: 2px;
                }
                .matrix-when {
                    font-size: 0.55rem; font-weight: 600; color: #9ca3af; line-height: 1;
                }
                .matrix-divider { grid-column: 1 / -1; border-top: 1px solid #f1f5f9; }
                .matrix-todaycol { background: rgba(99,102,241,0.05); border-radius: 8px; }
                .legend-chip {
                    display: inline-flex; align-items: center; gap: 4px;
                    font-size: 0.72rem; font-weight: 600; color: #4b5563;
                }
                .legend-dot {
                    width: 16px; height: 16px; border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 0.6rem; font-weight: 700;
                }
                .sheet-backdrop {
                    position: fixed; inset: 0; background: rgba(0,0,0,0.35);
                    z-index: 1200; display: flex; align-items: flex-end;
                }
                .sheet {
                    background: white; width: 100%; border-radius: 20px 20px 0 0;
                    padding: 1.25rem; max-height: 70vh; overflow-y: auto;
                    padding-bottom: calc(1.25rem + var(--safe-area-inset-bottom));
                }
                @media (min-width: 768px) {
                    .matrix-card { padding: 1rem; }
                    .matrix-grid { grid-template-columns: 170px repeat(7, 1fr); }
                    .matrix-rowlabel { font-size: 0.9rem; }
                    .sheet-backdrop { align-items: center; justify-content: center; }
                    .sheet { max-width: 480px; border-radius: 20px; }
                }
            `}</style>

            <div className="page-title-bar">
                <button className="btn border-0 p-0 me-3" onClick={() => navigate('/')}>
                    <i className="bi bi-chevron-left fs-4"></i>
                </button>
                <h5 className="mb-0 fw-bold">주간 점검 현황</h5>
            </div>

            {/* Week navigation */}
            <div className="d-flex align-items-center justify-content-between mb-3">
                <button className="btn btn-sm btn-white border rounded-circle btn-icon-circle" onClick={() => shiftWeek(-1)} aria-label="이전 주">
                    <i className="bi bi-chevron-left"></i>
                </button>
                <div className="text-center">
                    <div className="fw-bold">{data ? fmtDayRange(data.period.start, data.period.end) : '—'}</div>
                    {!isThisWeek && (
                        <button className="btn btn-link btn-sm p-0 text-decoration-none" onClick={() => setSearchParams({})}>
                            이번 주로
                        </button>
                    )}
                </div>
                <button className="btn btn-sm btn-white border rounded-circle btn-icon-circle" onClick={() => shiftWeek(1)} aria-label="다음 주">
                    <i className="bi bi-chevron-right"></i>
                </button>
            </div>

            {loading ? (
                <div className="text-center py-5 text-muted">불러오는 중…</div>
            ) : error ? (
                <div className="text-center py-5">
                    <div className="text-danger mb-2">{error}</div>
                    <button className="btn btn-sm btn-outline-secondary" onClick={fetchData}>다시 시도</button>
                </div>
            ) : !data || data.rows.length === 0 ? (
                <div className="text-center py-5 text-muted">
                    <i className="bi bi-calendar3 display-6 d-block mb-3 opacity-25"></i>
                    이 주에 예정된 점검이 없습니다.
                </div>
            ) : (
                <>
                    <div className="bg-white rounded-4 matrix-card shadow-sm border mb-3" style={{ borderColor: 'rgba(0,0,0,0.03)' }}>
                        <div className="matrix-scroll">
                            <div className="matrix-grid">
                                <div></div>
                                {DAY_LABELS.map((d, i) => {
                                    // 어느 날이 공휴일이었는지까지 보여준다.
                                    // '이번 주에 연휴가 있었다' 만으로는 어느 칸이
                                    // 비어 있는 게 당연한지 알 수 없다.
                                    const name = holidayByDay.get(data.columnDays?.[i]);
                                    return (
                                        <div
                                            key={d}
                                            className={`matrix-head ${i === todayIndex ? 'today' : ''} ${name ? 'holiday' : ''}`}
                                            title={name || undefined}
                                        >
                                            {d}
                                            {name && <span className="holiday-dot">·</span>}
                                        </div>
                                    );
                                })}

                                {data.rows.map((row) => (
                                    <React.Fragment key={row.planId}>
                                        <div className="matrix-divider"></div>
                                        <div className="matrix-rowlabel">
                                            {row.planName}
                                            {(() => {
                                                const names = rowFacilities(row);
                                                if (names.length === 0) return null;
                                                return (
                                                    <span className="target">
                                                        {names.length === 1 ? names[0] : `${names.length}개 시설`}
                                                    </span>
                                                );
                                            })()}
                                            <span className="sub">
                                                {row.period === 'DAY' ? '매일' : '주간'} {row.quota}회
                                                {row.floating.length > 0
                                                    && ` · 잔여 ${row.floating.map((f) => f.labelText).join('·')}`}
                                            </span>
                                        </div>
                                        {row.cells.map((cell, i) => (
                                            <div key={cell.date} className={`matrix-cell ${i === todayIndex ? 'matrix-todaycol' : ''}`}>
                                                {renderCell(row, cell, i)}
                                            </div>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>

                        {/* Beneath the grid, not at the foot of the page: a symbol
                            needs explaining where it is first met. */}
                        <div className="d-flex flex-wrap gap-2 pt-2 mt-1 border-top">
                            {['ON_TIME', 'LATE', 'MISSED', 'PENDING'].map((key) => (
                                <span key={key} className="legend-chip">
                                    <span className="legend-dot" style={{ background: STATE[key].bg, color: STATE[key].color }}>
                                        {STATE[key].symbol}
                                    </span>
                                    {STATE[key].bucketText || STATE[key].text}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Slots with no day of their own yet */}
                    {data.rows.some((r) => r.floating.length > 0) && (
                        <div className="bg-white rounded-4 p-3 shadow-sm border mb-3" style={{ borderColor: 'rgba(0,0,0,0.03)' }}>
                            <div className="fw-bold small mb-2">
                                <i className="bi bi-hourglass-split me-1 text-warning"></i>
                                아직 남은 점검
                            </div>
                            {data.rows.flatMap((row) =>
                                row.floating.map((slot) => (
                                    // 주 단위 계획은 끝나기 전까지 요일 칸에 들어가지
                                    // 않는다. 여기서 못 열면 어디가 남았는지 볼 데가
                                    // 아무 데도 없다.
                                    <div
                                        key={slot.id}
                                        className="d-flex align-items-center justify-content-between py-2 border-bottom"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => openDetail(`s${slot.id}`)}
                                    >
                                        <div style={{ minWidth: 0 }}>
                                            <div className="fw-medium small text-truncate">{row.planName}</div>
                                            <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                                                {routeText(slot)} · {slot.labelText}
                                                {slot.progress?.total > 1 &&
                                                    ` · ${slot.progress.done}/${slot.progress.total}곳`}
                                            </div>
                                        </div>
                                        <span
                                            className="badge rounded-pill ms-2"
                                            style={{
                                                background: slot.atRisk ? '#fef3c7' : '#f3f4f6',
                                                color: slot.atRisk ? '#b45309' : '#6b7280',
                                            }}
                                        >
                                            {slot.atRisk ? '마감 임박' : '대기'}
                                        </span>
                                    </div>
                                )),
                            )}
                        </div>
                    )}

                    {/* Week totals */}
                    <div className="bg-white rounded-4 p-3 shadow-sm border" style={{ borderColor: 'rgba(0,0,0,0.03)' }}>
                        <div className="d-flex justify-content-between align-items-baseline mb-2">
                            <span className="fw-bold small">주간 집계</span>
                            <span>
                                <span className="fw-bold" style={{ fontSize: '1.15rem' }}>{data.totals.completionRate}%</span>
                                <span className="text-muted ms-2" style={{ fontSize: '0.75rem' }}>
                                    정시 {data.totals.onTimeRate}%
                                </span>
                            </span>
                        </div>
                        <div className="d-flex flex-wrap gap-3">
                            {[
                                ['ON_TIME', data.totals.onTime],
                                ['LATE', data.totals.late],
                                ['MISSED', data.totals.missed],
                                ['PENDING', data.totals.pending],
                            ].map(([key, count]) => (
                                <span key={key} className="legend-chip">
                                    <span className="legend-dot" style={{ background: STATE[key].bg, color: STATE[key].color }}>
                                        {STATE[key].symbol}
                                    </span>
                                    {STATE[key].bucketText || STATE[key].text} <strong>{count}</strong>
                                </span>
                            ))}
                        </div>
                        {/* 대기 수가 표의 빈 칸보다 많아 보이는 이유를 적어 둔다.
                            회차는 그날이 와야 만들어지므로 아직 표에 자리가 없다.
                            그래도 이번 주에 해야 할 몫이라 분모에는 들어간다. */}
                        <div className="text-muted mt-2" style={{ fontSize: '0.72rem' }}>
                            대기에는 아직 날짜가 오지 않은 회차도 들어갑니다.
                        </div>
                        {/* 숫자를 고치지 않고 까닭만 말한다. 공휴일에 몇 회를
                            도는지는 회사마다 다르므로 우리가 정할 수 없다.
                            다만 연휴가 낀 주의 낮은 값을 '관리가 부실하다' 로
                            읽는 것은 막아야 한다 -- 건물주도 이 화면을 본다. */}
                        {holidays.length > 0 && (
                            <div className="mt-1" style={{ fontSize: '0.72rem', color: '#dc2626' }}>
                                <i className="bi bi-calendar-event me-1"></i>
                                이 주에는 {holidays.map((h) => h.name).join(' · ')}
                                {holidays.length > 1 ? ` 등 공휴일 ${holidays.length}일이` : '이'} 있습니다.
                                <span className="text-muted ms-1">
                                    공휴일에 못 채운 회차도 미이행으로 셉니다.
                                </span>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Cell detail */}
            {detail && (
                <div className="sheet-backdrop" onClick={closeDetail}>
                    <div className="sheet" onClick={(e) => e.stopPropagation()}>
                        <div className="d-flex justify-content-between align-items-start mb-3">
                            <div>
                                <div className="fw-bold">{detail.row.planName}</div>
                                <div className="text-muted small">
                                    {detail.cell === null
                                        ? '아직 날짜가 정해지지 않은 회차'
                                        : fmtDayWithWeekday(detail.cell.date)}
                                </div>
                            </div>
                            <button className="btn btn-sm border-0 p-0" onClick={closeDetail}>
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>

                        {detail.slots.map((slot) => {
                            const s = stateOf(slot);
                            return (
                                <div key={slot.id} className="p-3 rounded-3 mb-2" style={{ background: '#f9fafb' }}>
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                        <span className="legend-dot" style={{ background: s.bg, color: s.color }}>{s.symbol}</span>
                                        <span className="fw-bold small">{s.text}</span>
                                        <span className="text-muted small">· {slot.labelText}</span>
                                    </div>
                                    <div className="small text-muted mb-2">
                                        <div>{routeText(slot)}</div>
                                        {slot.completedAt && <div>완료: {fmtTime(slot.completedAt)}</div>}
                                        {slot.lateMinutes > 0 && (
                                            <div style={{ color: STATE.LATE.color }}>{fmtLateness(slot.lateMinutes)}</div>
                                        )}
                                    </div>

                                    {/* 곳마다 「기록」 을 눌러 오가면 열 곳짜리 회차는 스무 번을
                                        오간다. 다 볼 생각으로 들어온 사람에게는 이쪽이 입구다. */}
                                    {(slot.stops || []).some((stop) => stop.reportId) && (
                                        <button
                                            className="btn btn-sm btn-white border rounded-pill w-100 mb-2"
                                            style={{ fontSize: '0.8rem' }}
                                            onClick={() => navigate(`/rounds/${slot.id}`)}
                                        >
                                            <i className="bi bi-journal-text me-1"></i>
                                            기록 모두 보기
                                        </button>
                                    )}

                                    {/* 한 회차가 들른 곳을 전부 낸다. 예전에는 기록을 하나만
                                        보여줘서, 다섯 층을 돈 순회에서 나머지 넷을 볼 길이
                                        없었다. 안 들른 곳도 같이 나와야 무엇이 빠졌는지 보인다. */}
                                    {(slot.stops || []).map((stop) => (
                                        <div
                                            key={stop.id}
                                            className="d-flex align-items-center gap-2 py-1 border-top"
                                            style={{ fontSize: '0.8rem' }}
                                        >
                                            <span style={{ color: stop.completedAt ? STATE.ON_TIME.color : '#d1d5db' }}>
                                                {stop.completedAt ? '\u25CF' : '\u25CB'}
                                            </span>
                                            <span className="text-truncate">
                                                {stop.section?.name || `${stop.facility?.name} 전체`}
                                            </span>
                                            <span className="text-muted ms-auto text-nowrap">
                                                {stop.completedAt ? fmtTime(stop.completedAt) : '\u2014'}
                                            </span>
                                            {stop.reportId ? (
                                                <button
                                                    className="btn btn-sm border-0 p-0 text-primary"
                                                    style={{ fontSize: '0.8rem' }}
                                                    onClick={() => navigate(`/inspections/${stop.reportId}`)}
                                                >
                                                    기록
                                                </button>
                                            ) : (
                                                /* 여기서 바로 시작할 수 있어야 한다. 무엇이 남았는지
                                                   보고도 시설 목록으로 돌아가 다시 찾아 들어가게
                                                   하면, 남은 곳을 보여준 의미가 없다. */
                                                stop.facility?.id && (
                                                    <button
                                                        className="btn btn-sm btn-primary rounded-pill py-0 px-2"
                                                        style={{ fontSize: '0.75rem' }}
                                                        onClick={() =>
                                                            navigate(
                                                                `/facilities/${stop.facility.id}/inspect?stopId=${stop.stopId}`,
                                                            )
                                                        }
                                                    >
                                                        점검
                                                    </button>
                                                )
                                            )}
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default InspectionMatrixPage;
